/**
 * TradingPanel — ARC Terminal trading interface.
 * Supports paper trading and live trading (Hyperliquid via MetaMask, Jupiter via Phantom).
 */

import { formatPriceWithSymbol } from '@arc/shared';
import { memo, useState, useCallback } from 'react';
import { useTerminalStore } from '../../stores/terminal.store.js';
import { useTrading } from '../../hooks/useTrading.js';
import { useWallet }  from '../../hooks/useWallet.js';
import { calcPositionSize, calcTakeProfit } from '@arc/shared';
import type { OrderSide, OrderType, Position } from '@arc/shared';

// ─── Sub-components ────────────────────────────────────────────────────────────

function PnlBadge({ value, pct, size = 'sm' }: { value: number; pct?: number; size?: 'xs' | 'sm' | 'md' }) {
  const positive   = value >= 0;
  const sizeClass  = size === 'xs' ? 'text-[11px]' : size === 'md' ? 'text-[13px]' : 'text-[11px]';
  return (
    <span className={`font-mono font-bold ${sizeClass} ${positive ? 'text-buy' : 'text-sell'}`}>
      {positive ? '+' : ''}{value.toFixed(2)}
      {pct !== undefined && ` (${positive ? '+' : ''}${pct.toFixed(1)}%)`}
    </span>
  );
}

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className="border border-hud p-2 flex flex-col gap-0.5 bg-black/20"
         style={{ borderColor: accent ? 'rgba(0,212,255,0.3)' : undefined }}>
      <span className="font-mono text-[11px] tracking-widest opacity-90 uppercase">{label}</span>
      <span className="font-orb text-[12px] font-bold" style={{ color: 'var(--color-bright)' }}>{value}</span>
      {sub && <span className="font-mono text-[11px] opacity-90">{sub}</span>}
    </div>
  );
}

// ─── Exchange badge ─────────────────────────────────────────────────────────────

function ExchangeBadge({ exchange }: { exchange: 'hyperliquid' | 'jupiter' | 'paper' }) {
  const config = {
    hyperliquid: { label: 'HYPERLIQUID', color: '#00d4ff' },
    jupiter:     { label: 'JUPITER',     color: '#a78bfa' },
    paper:       { label: 'PAPER',       color: '#FFD700' },
  }[exchange];

  return (
    <span className="font-mono text-[11px] px-1.5 py-0.5 border tracking-wider"
          style={{
            color:       config.color,
            borderColor: config.color + '60',
            background:  config.color + '15',
            clipPath:    'polygon(3px 0%,100% 0%,calc(100% - 3px) 100%,0% 100%)',
          }}>
      {config.label}
    </span>
  );
}

// ─── Order Form ────────────────────────────────────────────────────────────────

function OrderForm() {
  const tokens = useTerminalStore(s => s.tokens);
  const prices = useTerminalStore(s => s.prices);
  const {
    submitOrder, paperAccount, liveAccount,
    tradeMode, activeWallet, isSubmitting, lastTradeError, clearTradeError,
  } = useTrading();

  const [symbol,   setSymbol]   = useState('BTC');
  const [side,     setSide]     = useState<OrderSide>('buy');
  const [type,     setType]     = useState<OrderType>('market');
  const [size,     setSize]     = useState('100');
  const [leverage, setLeverage] = useState(1);
  const [limitPx,  setLimitPx]  = useState('');
  const [slPx,     setSlPx]     = useState('');
  const [tpPx,     setTpPx]     = useState('');
  const [useRiskPct, setUseRiskPct] = useState(false);
  const [riskPct,  setRiskPct]  = useState('1');
  const [flash,    setFlash]    = useState<'buy' | 'sell' | null>(null);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const curPrice   = prices[symbol]?.priceUsd ?? 0;
  const isLive     = tradeMode === 'live';
  const isPhantom  = activeWallet?.type === 'phantom';
  const isMetaMask = activeWallet?.type === 'metamask';

  // Determine exchange label based on wallet
  const exchange = !isLive ? 'paper' : isPhantom ? 'jupiter' : 'hyperliquid';

  // Available balance
  const availableBalance = isLive
    ? (liveAccount?.available ?? activeWallet?.usdBalance ?? 0)
    : paperAccount.balance;

  // Leverage: Jupiter is spot-only (no leverage); HL supports up to 50x
  const maxLeverage = isPhantom ? 1 : isMetaMask ? 50 : 20;

  // Derived SL/TP values
  const slNum   = parseFloat(slPx)  || undefined;
  const tpNum   = parseFloat(tpPx)  || undefined;

  // Risk % auto-size: compute size from risk%, account equity, and SL distance
  const autoSize = useRiskPct && slNum && curPrice > 0
    ? calcPositionSize(
        isLive ? (liveAccount?.equity ?? 0) : paperAccount.equity,
        parseFloat(riskPct) || 1,
        curPrice,
        slNum,
      )
    : null;

  // Auto-fill TP from SL using 2:1 R/R when SL is entered and TP is empty
  const autoTp = slNum && tpPx === '' && curPrice > 0
    ? calcTakeProfit(curPrice, slNum, side, 2)
    : null;

  const effectiveSize = autoSize !== null ? autoSize.toFixed(2) : size;
  const effectiveTp   = tpNum ?? autoTp ?? undefined;

  // R/R display
  const rrRatio = slNum && effectiveTp && curPrice > 0
    ? Math.abs(effectiveTp - curPrice) / Math.abs(slNum - curPrice)
    : null;

  const sizeButtons = [25, 50, 100, 250];

  const handleSubmit = useCallback(async () => {
    setLocalErr(null);
    clearTradeError();

    // Validate
    const sizeNum = parseFloat(effectiveSize);
    if (isNaN(sizeNum) || sizeNum <= 0) { setLocalErr('Invalid size'); return; }

    if (isLive && !activeWallet) {
      setLocalErr('Connect a wallet first');
      return;
    }

    if (isLive && sizeNum > availableBalance && availableBalance > 0) {
      setLocalErr('Insufficient balance');
      return;
    }

    const limitNum = type === 'limit' ? parseFloat(limitPx) : undefined;
    if (type === 'limit' && (isNaN(limitNum!) || limitNum! <= 0)) {
      setLocalErr('Invalid limit price');
      return;
    }

    const order = await submitOrder({
      symbol,
      side,
      type,
      size:     sizeNum,
      leverage: isPhantom ? 1 : leverage,
      ...(limitNum  !== undefined ? { price:      limitNum  } : {}),
      ...(slNum     !== undefined ? { stopLoss:   slNum     } : {}),
      ...(effectiveTp !== undefined ? { takeProfit: effectiveTp } : {}),
    });

    if (order) {
      setFlash(side);
      setTimeout(() => setFlash(null), 600);
    }
  }, [
    symbol, side, type, effectiveSize, leverage, limitPx, slNum, effectiveTp,
    availableBalance, isLive, isPhantom, activeWallet,
    submitOrder, clearTradeError,
  ]);

  const displayErr = localErr ?? lastTradeError;

  return (
    <div className="flex flex-col gap-2 p-2">

      {/* Mode + exchange badge */}
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {isLive && (
          <span className="font-mono text-[11px] tracking-widest px-2 py-0.5 border animate-glow-pulse"
                style={{ borderColor: 'var(--color-buy)', color: 'var(--color-buy)' }}>
            ◉ LIVE TRADING
          </span>
        )}
        {exchange !== 'paper' && <ExchangeBadge exchange={exchange} />}
        {isLive && !activeWallet && (
          <span className="font-mono text-[11px] text-sell opacity-70">↑ connect wallet</span>
        )}
      </div>

      {/* Symbol select */}
      <div className="flex flex-col gap-1">
        <label className="font-mono text-[11px] tracking-widest opacity-90">SYMBOL</label>
        <select
          value={symbol}
          onChange={e => setSymbol(e.target.value)}
          className="bg-black/40 border border-hud font-orb font-bold text-[11px] px-2 py-1.5 outline-none cursor-pointer w-full"
          style={{ color: 'var(--color-arc)' }}
        >
          {tokens.map(t => (
            <option key={t.symbol} value={t.symbol} className="bg-[#040d1a]">
              {t.symbol}{prices[t.symbol]?.priceUsd ? `  ${formatPriceWithSymbol(prices[t.symbol]!.priceUsd)}` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Side toggle */}
      <div className="grid grid-cols-2 gap-1">
        {(['buy', 'sell'] as OrderSide[]).map(s => (
          <button
            key={s}
            onClick={() => setSide(s)}
            disabled={isSubmitting}
            className={`font-orb font-bold text-[11px] py-2 tracking-widest border transition-all duration-150 ${
              side === s
                ? s === 'buy'
                  ? 'bg-buy/20 border-buy text-buy'
                  : 'bg-sell/20 border-sell text-sell'
                : 'border-hud hover:border-arc'
            } ${flash === s ? 'animate-pulse' : ''} disabled:opacity-30 disabled:cursor-not-allowed`}
            style={{ color: side === s ? undefined : 'var(--color-bright)' }}
          >
            {isPhantom
              ? (s === 'buy' ? '⇄ BUY' : '⇄ SELL')
              : (s === 'buy' ? '▲ LONG' : '▼ SHORT')
            }
          </button>
        ))}
      </div>

      {/* Order type — Jupiter is spot-only (market only) */}
      {!isPhantom && (
        <div className="grid grid-cols-2 gap-1">
          {(['market', 'limit'] as OrderType[]).map(t => (
            <button
              key={t}
              onClick={() => setType(t)}
              disabled={isSubmitting}
              className={`font-mono text-[11px] py-1 border tracking-widest transition-all disabled:opacity-30 ${
                type === t ? 'border-arc text-arc bg-arc/10' : 'border-hud hover:border-arc'
              }`}
              style={{ color: type === t ? 'var(--color-arc)' : 'var(--color-bright)' }}
            >
              {t.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {/* Size */}
      <div className="flex flex-col gap-1">
        <label className="font-mono text-[11px] tracking-widest opacity-90">SIZE (USD)</label>
        <input
          type="number"
          value={size}
          onChange={e => setSize(e.target.value)}
          disabled={isSubmitting}
          className="bg-black/40 border border-hud font-mono text-[11px] px-2 py-1.5 outline-none w-full disabled:opacity-40"
          style={{ color: 'var(--color-bright)' }}
          placeholder="100"
          min="1"
        />
        <div className="grid grid-cols-4 gap-1">
          {sizeButtons.map(s => (
            <button
              key={s}
              onClick={() => setSize(String(s))}
              disabled={isSubmitting}
              className="font-mono text-[11px] py-0.5 border border-hud opacity-50 hover:opacity-80 hover:border-arc transition-all disabled:opacity-20"
              style={{ color: 'var(--color-bright)' }}
            >
              ${s}
            </button>
          ))}
        </div>
      </div>

      {/* Leverage (hidden for Phantom/Jupiter) */}
      {!isPhantom && (
        <div className="flex flex-col gap-1">
          <div className="flex justify-between items-center">
            <label className="font-mono text-[11px] tracking-widest opacity-90">LEVERAGE</label>
            <span className="font-orb font-bold text-[11px]" style={{ color: 'var(--color-arc)' }}>
              {leverage}x
            </span>
          </div>
          <input
            type="range"
            min="1" max={maxLeverage} step="1"
            value={leverage}
            onChange={e => setLeverage(Number(e.target.value))}
            disabled={isSubmitting}
            className="w-full h-1 disabled:opacity-40"
          />
          <div className="flex justify-between font-mono text-[11px] opacity-90">
            <span>1x</span>
            <span>{Math.round(maxLeverage * 0.25)}x</span>
            <span>{Math.round(maxLeverage * 0.5)}x</span>
            <span>{maxLeverage}x</span>
          </div>
        </div>
      )}

      {/* Limit price */}
      {type === 'limit' && !isPhantom && (
        <div className="flex flex-col gap-1">
          <label className="font-mono text-[11px] tracking-widest opacity-90">LIMIT PRICE</label>
          <input
            type="number"
            value={limitPx}
            onChange={e => setLimitPx(e.target.value)}
            disabled={isSubmitting}
            className="bg-black/40 border border-accent/60 font-mono text-[11px] px-2 py-1.5 outline-none w-full disabled:opacity-40"
            style={{ color: 'var(--color-accent)' }}
            placeholder={curPrice.toFixed(2)}
          />
          {curPrice > 0 && (
            <span className="font-mono text-[11px] opacity-90">Mark: ${curPrice.toFixed(2)}</span>
          )}
        </div>
      )}

      {/* ── SL / TP (paper mode only) ── */}
      {!isPhantom && (
        <div className="flex flex-col gap-1 border border-hud/40 p-1.5">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] tracking-widest opacity-90">STOP LOSS / TAKE PROFIT</span>
            {rrRatio !== null && (
              <span className="font-mono text-[11px] opacity-90"
                    style={{ color: rrRatio >= 2 ? 'var(--color-buy)' : 'var(--color-bright)' }}>
                R/R {rrRatio.toFixed(1)}:1
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-1">
            {/* Stop Loss */}
            <div className="flex flex-col gap-0.5">
              <label className="font-mono text-[11px] text-sell opacity-90">SL</label>
              <input
                type="number"
                value={slPx}
                onChange={e => setSlPx(e.target.value)}
                disabled={isSubmitting}
                className="bg-black/40 border border-sell/30 font-mono text-[11px] px-1.5 py-1 outline-none w-full disabled:opacity-40"
                style={{ color: 'var(--color-sell)' }}
                placeholder="Price"
                step="any"
                min="0"
              />
            </div>

            {/* Take Profit */}
            <div className="flex flex-col gap-0.5">
              <label className="font-mono text-[11px] text-buy opacity-90">
                TP {autoTp && tpPx === '' ? <span className="opacity-40">(auto 2:1)</span> : null}
              </label>
              <input
                type="number"
                value={tpPx || (autoTp ? autoTp.toFixed(curPrice < 1 ? 6 : 2) : '')}
                onChange={e => setTpPx(e.target.value)}
                disabled={isSubmitting}
                className="bg-black/40 border border-buy/30 font-mono text-[11px] px-1.5 py-1 outline-none w-full disabled:opacity-40"
                style={{ color: 'var(--color-buy)' }}
                placeholder="Price"
                step="any"
                min="0"
              />
            </div>
          </div>

          {/* Risk % auto-size toggle */}
          <label className="flex items-center gap-2 cursor-pointer mt-0.5">
            <input
              type="checkbox"
              checked={useRiskPct}
              onChange={e => setUseRiskPct(e.target.checked)}
              className="accent-arc"
            />
            <span className="font-mono text-[11px] opacity-90">Risk %</span>
            {useRiskPct && (
              <input
                type="number"
                value={riskPct}
                onChange={e => setRiskPct(e.target.value)}
                className="w-12 bg-black/40 border border-hud font-mono text-[11px] px-1 py-0.5 outline-none"
                style={{ color: 'var(--color-arc)' }}
                min="0.1"
                max="100"
                step="0.1"
              />
            )}
            {useRiskPct && autoSize !== null && (
              <span className="font-mono text-[11px] opacity-90 ml-auto">→ ${autoSize.toFixed(0)}</span>
            )}
          </label>
        </div>
      )}

      {/* Estimated notional */}
      {parseFloat(effectiveSize) > 0 && leverage > 1 && !isPhantom && (
        <div className="border border-hud p-1.5 bg-black/20 font-mono text-[11px] flex justify-between">
          <span className="opacity-40">Notional</span>
          <span style={{ color: 'var(--color-bright)' }}>${(parseFloat(effectiveSize) * leverage).toFixed(2)}</span>
        </div>
      )}

      {/* Jupiter swap info */}
      {isPhantom && parseFloat(size) > 0 && (
        <div className="border border-hud p-1.5 bg-black/20 font-mono text-[11px] flex justify-between">
          <span className="opacity-40">Swap (0.5% slippage)</span>
          <span style={{ color: 'var(--color-arc)' }}>via Jupiter v6</span>
        </div>
      )}

      {/* Error */}
      {displayErr && (
        <div className="font-mono text-[11px] text-sell border border-sell/30 px-2 py-1 bg-sell/10 flex items-center gap-1">
          <span>⚠</span>
          <span>{displayErr}</span>
          <button onClick={() => { setLocalErr(null); clearTradeError(); }}
                  className="ml-auto opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Submit button */}
      <button
        onClick={handleSubmit}
        disabled={isSubmitting || (isLive && !activeWallet)}
        className={`font-orb font-bold text-[11px] py-2.5 tracking-widest border transition-all
          hover:scale-[1.01] active:scale-[0.99]
          disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
          ${side === 'buy'
            ? 'bg-buy/15 border-buy text-buy hover:bg-buy/25'
            : 'bg-sell/15 border-sell text-sell hover:bg-sell/25'
          }`}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
            {isPhantom ? 'SWAPPING…' : 'SIGNING…'}
          </span>
        ) : isLive && !activeWallet ? (
          'CONNECT WALLET'
        ) : isPhantom ? (
          side === 'buy' ? `⇄ BUY ${symbol}` : `⇄ SELL ${symbol}`
        ) : (
          side === 'buy' ? '▲ PLACE LONG' : '▼ PLACE SHORT'
        )}
      </button>

      {/* Balance */}
      <div className="font-mono text-[11px] opacity-90 text-right">
        {isLive ? 'Available' : 'Balance'}:{' '}
        <span style={{ color: 'var(--color-bright)' }}>
          {availableBalance > 0 ? `$${availableBalance.toFixed(2)}` : '—'}
        </span>
        {isLive && activeWallet && (
          <span className="ml-2 opacity-60">
            {activeWallet.address.slice(0, 4)}…{activeWallet.address.slice(-4)}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Position row ───────────────────────────────────────────────────────────────

function PositionRow({ pos, onClose }: { pos: Position; onClose: () => void }) {
  const isProfit = pos.unrealizedPnl >= 0;
  const fmtPx    = (p: number) => p > 100 ? `$${p.toFixed(1)}` : p > 0.01 ? `$${p.toFixed(4)}` : `$${p.toFixed(8)}`;

  return (
    <div className="border p-2 flex flex-col gap-1 transition-all hover:border-opacity-80"
         style={{ borderColor: isProfit ? 'rgba(0,255,136,0.2)' : 'rgba(255,60,60,0.2)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={`font-mono text-[11px] font-bold ${pos.side === 'buy' ? 'text-buy' : 'text-sell'}`}>
            {pos.side === 'buy' ? '▲' : '▼'}
          </span>
          <span className="font-orb font-bold text-[11px]" style={{ color: 'var(--color-bright)' }}>
            {pos.symbol}
          </span>
          <span className="font-mono text-[11px] border border-hud px-1 opacity-90">{pos.leverage}x</span>
          {pos.mode === 'live' && <ExchangeBadge exchange={pos.exchange as 'hyperliquid' | 'jupiter' | 'paper'} />}
        </div>
        {pos.mode === 'paper' && (
          <button
            onClick={onClose}
            className="font-mono text-[11px] border border-sell/40 text-sell px-1.5 py-0.5 hover:bg-sell/10 transition-colors"
          >
            CLOSE
          </button>
        )}
        {pos.mode === 'live' && (
          <span className="font-mono text-[11px] opacity-90 border border-hud px-1 py-0.5">LIVE</span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-1 font-mono text-[11px]">
        <div>
          <div className="opacity-90 text-[11px]">SIZE</div>
          <div style={{ color: 'var(--color-bright)' }}>${pos.size.toFixed(0)}</div>
        </div>
        <div>
          <div className="opacity-90 text-[11px]">ENTRY</div>
          <div style={{ color: 'var(--color-text)' }}>{fmtPx(pos.entryPrice)}</div>
        </div>
        <div>
          <div className="opacity-90 text-[11px]">MARK</div>
          <div style={{ color: 'var(--color-bright)' }}>{fmtPx(pos.currentPrice)}</div>
        </div>
      </div>

      {/* SL / TP badges */}
      {(pos.stopLoss !== undefined || pos.takeProfit !== undefined) && (
        <div className="flex items-center gap-2 font-mono text-[11px]">
          {pos.stopLoss !== undefined && (
            <span className="flex items-center gap-0.5 border border-sell/30 px-1 py-0.5 bg-sell/5"
                  style={{ color: 'var(--color-sell)' }}>
              ✕ SL {fmtPx(pos.stopLoss)}
            </span>
          )}
          {pos.takeProfit !== undefined && (
            <span className="flex items-center gap-0.5 border border-buy/30 px-1 py-0.5 bg-buy/5"
                  style={{ color: 'var(--color-buy)' }}>
              ✓ TP {fmtPx(pos.takeProfit)}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-hud pt-1">
        <span className="font-mono text-[11px] opacity-90">UNREALIZED P&L</span>
        <PnlBadge value={pos.unrealizedPnl} pct={pos.unrealizedPnlPct} size="sm" />
      </div>
    </div>
  );
}

// ─── Order history row ──────────────────────────────────────────────────────────

function OrderRow({ order }: { order: ReturnType<typeof useTrading>['recentFills'][number] }) {
  const ts = new Date(order.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="flex items-center justify-between px-2 py-1 border-b border-hud/50 font-mono text-[11px]">
      <span className={`${order.side === 'buy' ? 'text-buy' : 'text-sell'}`}>
        {order.side === 'buy' ? '▲' : '▼'} {order.symbol}
      </span>
      <span className="opacity-90 text-[11px]">{order.exchange.toUpperCase().slice(0, 4)}</span>
      <span style={{ color: 'var(--color-text)' }}>${(order.fillPrice ?? 0).toFixed(2)}</span>
      <span style={{ color: 'var(--color-bright)' }}>{ts}</span>
      {order.pnl !== undefined && <PnlBadge value={order.pnl} size="xs" />}
    </div>
  );
}

// ─── Main panel ─────────────────────────────────────────────────────────────────

type Tab = 'order' | 'positions' | 'history';

export const TradingPanel = memo(function TradingPanel() {
  const [tab, setTab] = useState<Tab>('order');
  const {
    positions, openOrders, recentFills,
    paperAccount, liveAccount,
    totalUnrealizedPnl, winRate,
    closePosition, cancelOrder, resetPaperAccount, tradeMode,
  } = useTrading();
  const { connectPhantom, connectMetaMask, isPhantomAvailable, isMetaMaskAvailable } = useWallet();
  const setTradeMode = useTerminalStore(s => s.setTradeMode);
  const activeWallet = useTerminalStore(s => s.activeWallet);

  const isLive = tradeMode === 'live';

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'order',     label: 'ORDER'     },
    { id: 'positions', label: 'POSITIONS', count: positions.length   },
    { id: 'history',   label: 'HISTORY',   count: recentFills.length },
  ];

  // Account stats to show (live vs paper)
  const equity       = isLive ? (liveAccount?.equity ?? 0)    : paperAccount.equity;
  const balance      = isLive ? (liveAccount?.available ?? 0)  : paperAccount.balance;
  const totalPnl     = isLive ? 0                              : paperAccount.totalPnl;
  const equityLabel  = isLive ? 'HL EQUITY'                    : 'EQUITY';
  const balanceLabel = isLive ? 'AVAILABLE'                    : 'BALANCE';

  return (
    <div className="h-full flex flex-col overflow-hidden">

      {/* ── Mode toggle ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between px-2 py-1.5 border-b border-hud flex-shrink-0 gap-2">
        <div className="flex flex-wrap gap-1">
          {(['paper', 'live'] as const).map(m => (
            <button
              key={m}
              onClick={() => setTradeMode(m)}
              className={`font-mono text-[11px] px-2 py-0.5 border tracking-widest transition-all whitespace-nowrap ${
                tradeMode === m
                  ? m === 'paper'
                    ? 'border-accent text-accent bg-accent/10'
                    : 'border-buy text-buy bg-buy/10'
                  : 'border-hud hover:border-arc'
              }`}
              style={tradeMode === m ? undefined : { color: 'var(--color-bright)' }}
              aria-pressed={tradeMode === m}
              title={m === 'paper' ? 'Paper trading (simulated)' : 'Live trading (real funds)'}
            >
              {m === 'paper' ? '◈ PAPER' : '◉ LIVE'}
            </button>
          ))}
        </div>

        {/* Wallet quick-connect buttons when in live mode */}
        {isLive && !activeWallet && (
          <div className="flex gap-1">
            <button
              onClick={connectPhantom}
              className="font-mono text-[11px] px-1.5 py-0.5 border border-hud opacity-60 hover:opacity-90 hover:border-arc transition-all"
              style={{ color: 'var(--color-arc)' }}
              title="Connect Phantom"
            >
              {isPhantomAvailable ? '◎ PHT' : '↗ PHANTOM'}
            </button>
            <button
              onClick={connectMetaMask}
              className="font-mono text-[11px] px-1.5 py-0.5 border border-hud opacity-60 hover:opacity-90 hover:border-accent transition-all"
              style={{ color: 'var(--color-accent)' }}
              title="Connect MetaMask"
            >
              {isMetaMaskAvailable ? '⬡ MM' : '↗ METAMASK'}
            </button>
          </div>
        )}

        {isLive && activeWallet && (
          <div className="flex items-center gap-1">
            <ExchangeBadge exchange={activeWallet.type === 'phantom' ? 'jupiter' : 'hyperliquid'} />
            <span className="font-mono text-[11px] opacity-90">
              {activeWallet.address.slice(0, 4)}…{activeWallet.address.slice(-4)}
            </span>
          </div>
        )}
      </div>

      {/* ── Account stats ─────────────────────────────────────────────────────── */}
      <div className="grid gap-1 p-2 flex-shrink-0 border-b border-hud" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(84px, 1fr))' }}>
        <StatCard
          label={equityLabel}
          value={equity > 0 ? `$${equity.toFixed(0)}` : '—'}
          sub={`${balanceLabel} $${balance.toFixed(0)}`}
          accent={isLive}
        />
        <StatCard
          label="TOTAL P&L"
          value={totalPnl !== 0
            ? `${totalPnl >= 0 ? '+' : ''}$${totalPnl.toFixed(2)}`
            : isLive ? '—' : '$0.00'}
          sub={isLive ? 'HL account' : `${winRate}% win rate`}
        />
        <StatCard
          label="UNREALIZED"
          value={`${totalUnrealizedPnl >= 0 ? '+' : ''}$${totalUnrealizedPnl.toFixed(2)}`}
          sub={`${positions.length} open`}
        />
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap border-b border-hud flex-shrink-0">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 min-w-fit whitespace-nowrap px-2 font-mono text-[11px] py-1.5 tracking-widest border-b-2 transition-all ${
              tab === t.id ? 'text-arc border-arc' : 'border-transparent hover:border-arc/50'
            }`}
            style={{ color: tab === t.id ? 'var(--color-arc)' : 'var(--color-bright)' }}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className="ml-1">({t.count})</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab content ───────────────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === 'order' && <OrderForm />}

        {tab === 'positions' && (
          <div className="p-2 flex flex-col gap-1.5">
            {positions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 gap-2 opacity-30">
                <div className="text-[24px]">⬡</div>
                <span className="font-mono text-[11px] tracking-widest">NO OPEN POSITIONS</span>
              </div>
            ) : (
              <>
                {positions.map(pos => (
                  <PositionRow key={pos.id} pos={pos} onClose={() => closePosition(pos.id)} />
                ))}
                {positions.some(p => p.mode === 'paper') && (
                  <button
                    onClick={() => positions.filter(p => p.mode === 'paper').forEach(p => closePosition(p.id))}
                    className="font-mono text-[11px] border border-sell/40 text-sell py-1 hover:bg-sell/10 transition-colors mt-1 tracking-widest"
                  >
                    ✕ CLOSE ALL PAPER
                  </button>
                )}
              </>
            )}

            {openOrders.length > 0 && (
              <div className="mt-2">
                <div className="font-mono text-[11px] opacity-90 tracking-widest mb-1">PENDING ORDERS</div>
                {openOrders.map(o => (
                  <div key={o.id} className="flex items-center justify-between border border-hud/40 px-2 py-1 font-mono text-[11px] mb-1">
                    <span className={o.side === 'buy' ? 'text-buy' : 'text-sell'}>
                      {o.side === 'buy' ? '▲' : '▼'} {o.symbol}
                    </span>
                    <span className="opacity-50">LIMIT @ ${o.price?.toFixed(2)}</span>
                    <button onClick={() => cancelOrder(o.id)} className="text-sell hover:opacity-80 text-[11px]">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div className="flex flex-col">
            {recentFills.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 gap-2 opacity-30">
                <div className="text-[24px]">⬡</div>
                <span className="font-mono text-[11px] tracking-widest">NO TRADE HISTORY</span>
              </div>
            ) : (
              recentFills.map(o => <OrderRow key={o.id} order={o} />)
            )}
            {recentFills.length > 0 && (
              <button
                onClick={resetPaperAccount}
                className="font-mono text-[11px] border border-hud m-2 py-1 opacity-40 hover:opacity-70 hover:border-sell hover:text-sell transition-all tracking-widest"
              >
                ↺ RESET PAPER ACCOUNT
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
